import { API_BASE_URL } from '../config/constants';
import type {
  ApiResponse,
  SuccessStory,
  CompleteSuccessStory,
  ImpactMetric,
  CustomerQuote,
  Differentiator,
  ImplementationPhase,
  RoiMetric,
  ProblemStatement,
  CreateSuccessStoryInput,
  CreateImpactMetricInput,
  CreateCustomerQuoteInput,
  CreateDifferentiatorInput,
  CreateImplementationPhaseInput,
  CreateRoiMetricInput,
  CreateProblemStatementInput,
} from '../types';

class ApiService {
  // ============================================
  // Success Stories API
  // ============================================
  async getAllStories(): Promise<ApiResponse<SuccessStory[]>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories`);
    return response.json() as Promise<ApiResponse<SuccessStory[]>>;
  }

  async getPublishedStories(): Promise<ApiResponse<SuccessStory[]>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories/published`);
    return response.json() as Promise<ApiResponse<SuccessStory[]>>;
  }

  async getStoryById(id: string): Promise<ApiResponse<CompleteSuccessStory>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories/${id}`);
    return response.json() as Promise<ApiResponse<CompleteSuccessStory>>;
  }

  async getStoryBySlug(slug: string): Promise<ApiResponse<CompleteSuccessStory>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories/slug/${slug}`);
    return response.json() as Promise<ApiResponse<CompleteSuccessStory>>;
  }

  async createStory(data: CreateSuccessStoryInput): Promise<ApiResponse<SuccessStory>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json() as Promise<ApiResponse<SuccessStory>>;
  }

  async updateStory(
    id: string,
    data: Partial<CreateSuccessStoryInput>
  ): Promise<ApiResponse<SuccessStory>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json() as Promise<ApiResponse<SuccessStory>>;
  }

  async deleteStory(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories/${id}`, {
      method: 'DELETE',
    });
    return response.json() as Promise<ApiResponse<{ deleted: boolean }>>;
  }

  // ============================================
  // Impact Metrics API
  // ============================================
  async getMetricsByStoryId(storyId: string): Promise<ApiResponse<ImpactMetric[]>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories/${storyId}/metrics`);
    return response.json() as Promise<ApiResponse<ImpactMetric[]>>;
  }

  async createMetric(
    storyId: string,
    data: CreateImpactMetricInput
  ): Promise<ApiResponse<ImpactMetric>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories/${storyId}/metrics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json() as Promise<ApiResponse<ImpactMetric>>;
  }

  async updateMetric(
    id: string,
    data: Partial<CreateImpactMetricInput>
  ): Promise<ApiResponse<ImpactMetric>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/metrics/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json() as Promise<ApiResponse<ImpactMetric>>;
  }

  async deleteMetric(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/metrics/${id}`, {
      method: 'DELETE',
    });
    return response.json() as Promise<ApiResponse<{ deleted: boolean }>>;
  }

  // ============================================
  // Customer Quotes API
  // ============================================
  async getQuotesByStoryId(storyId: string): Promise<ApiResponse<CustomerQuote[]>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories/${storyId}/quotes`);
    return response.json() as Promise<ApiResponse<CustomerQuote[]>>;
  }

  async createQuote(
    storyId: string,
    data: CreateCustomerQuoteInput
  ): Promise<ApiResponse<CustomerQuote>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories/${storyId}/quotes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json() as Promise<ApiResponse<CustomerQuote>>;
  }

  async updateQuote(
    id: string,
    data: Partial<CreateCustomerQuoteInput>
  ): Promise<ApiResponse<CustomerQuote>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/quotes/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json() as Promise<ApiResponse<CustomerQuote>>;
  }

  async deleteQuote(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/quotes/${id}`, {
      method: 'DELETE',
    });
    return response.json() as Promise<ApiResponse<{ deleted: boolean }>>;
  }

  // ============================================
  // Differentiators API
  // ============================================
  async getDifferentiatorsByStoryId(storyId: string): Promise<ApiResponse<Differentiator[]>> {
    const response = await fetch(
      `${API_BASE_URL}/api/success-stories/stories/${storyId}/differentiators`
    );
    return response.json() as Promise<ApiResponse<Differentiator[]>>;
  }

  async createDifferentiator(
    storyId: string,
    data: CreateDifferentiatorInput
  ): Promise<ApiResponse<Differentiator>> {
    const response = await fetch(
      `${API_BASE_URL}/api/success-stories/stories/${storyId}/differentiators`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
    return response.json() as Promise<ApiResponse<Differentiator>>;
  }

  async updateDifferentiator(
    id: string,
    data: Partial<CreateDifferentiatorInput>
  ): Promise<ApiResponse<Differentiator>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/differentiators/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json() as Promise<ApiResponse<Differentiator>>;
  }

  async deleteDifferentiator(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/differentiators/${id}`, {
      method: 'DELETE',
    });
    return response.json() as Promise<ApiResponse<{ deleted: boolean }>>;
  }

  // ============================================
  // Implementation Phases API
  // ============================================
  async getPhasesByStoryId(storyId: string): Promise<ApiResponse<ImplementationPhase[]>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories/${storyId}/phases`);
    return response.json() as Promise<ApiResponse<ImplementationPhase[]>>;
  }

  async createPhase(
    storyId: string,
    data: CreateImplementationPhaseInput
  ): Promise<ApiResponse<ImplementationPhase>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/stories/${storyId}/phases`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json() as Promise<ApiResponse<ImplementationPhase>>;
  }

  async updatePhase(
    id: string,
    data: Partial<CreateImplementationPhaseInput>
  ): Promise<ApiResponse<ImplementationPhase>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/phases/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json() as Promise<ApiResponse<ImplementationPhase>>;
  }

  async deletePhase(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/phases/${id}`, {
      method: 'DELETE',
    });
    return response.json() as Promise<ApiResponse<{ deleted: boolean }>>;
  }

  // ============================================
  // ROI Metrics API
  // ============================================
  async getRoiMetricsByStoryId(storyId: string): Promise<ApiResponse<RoiMetric[]>> {
    const response = await fetch(
      `${API_BASE_URL}/api/success-stories/stories/${storyId}/roi-metrics`
    );
    return response.json() as Promise<ApiResponse<RoiMetric[]>>;
  }

  async createRoiMetric(
    storyId: string,
    data: CreateRoiMetricInput
  ): Promise<ApiResponse<RoiMetric>> {
    const response = await fetch(
      `${API_BASE_URL}/api/success-stories/stories/${storyId}/roi-metrics`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
    return response.json() as Promise<ApiResponse<RoiMetric>>;
  }

  async updateRoiMetric(
    id: string,
    data: Partial<CreateRoiMetricInput>
  ): Promise<ApiResponse<RoiMetric>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/roi-metrics/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json() as Promise<ApiResponse<RoiMetric>>;
  }

  async deleteRoiMetric(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/roi-metrics/${id}`, {
      method: 'DELETE',
    });
    return response.json() as Promise<ApiResponse<{ deleted: boolean }>>;
  }

  // ============================================
  // Problem Statements API
  // ============================================
  async getProblemStatementsByStoryId(storyId: string): Promise<ApiResponse<ProblemStatement[]>> {
    const response = await fetch(
      `${API_BASE_URL}/api/success-stories/stories/${storyId}/problems`
    );
    return response.json() as Promise<ApiResponse<ProblemStatement[]>>;
  }

  async createProblemStatement(
    storyId: string,
    data: CreateProblemStatementInput
  ): Promise<ApiResponse<ProblemStatement>> {
    const response = await fetch(
      `${API_BASE_URL}/api/success-stories/stories/${storyId}/problems`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
    return response.json() as Promise<ApiResponse<ProblemStatement>>;
  }

  async updateProblemStatement(
    id: string,
    data: Partial<CreateProblemStatementInput>
  ): Promise<ApiResponse<ProblemStatement>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/problems/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json() as Promise<ApiResponse<ProblemStatement>>;
  }

  async deleteProblemStatement(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    const response = await fetch(`${API_BASE_URL}/api/success-stories/problems/${id}`, {
      method: 'DELETE',
    });
    return response.json() as Promise<ApiResponse<{ deleted: boolean }>>;
  }
}

export const apiService = new ApiService();
